import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Button } from "@mui/material";
import { ClioService, ReportModel } from "../../../api";
import { MatterData } from "../../../api/models/MatterData";
import { ReportNoteRequest } from "../../../api/models/ReportNoteRequest";
import { ReportNoteResponse } from "../../../api/models/ReportNoteResponse";

type ReportLevel2 = NonNullable<ReportModel["reportLevel2"]>[number];

type RowWithSpans = ReportLevel2 & {
  _groupRowSpan?: number;
  _skipTeam?: boolean;
  _skipMatter?: boolean;
  _skipUpcoming?: boolean;
  _skipNote?: boolean;
};

const normalizeText = (v: unknown): string => {
  if (v === null || v === undefined) return "";
  return String(v).replace(/\r\n|\n\r|\r/g, "\n");
};

const computeMatterGroupSpans = (rows: ReportLevel2[]): RowWithSpans[] => {
  const out: RowWithSpans[] = [];
  let i = 0;

  while (i < rows.length) {
    const matter = rows[i]?.level2Data2 ?? "";
    let j = i + 1;
    while (j < rows.length && (rows[j]?.level2Data2 ?? "") === matter) j++;

    const span = j - i;

    for (let k = i; k < j; k++) {
      const base: RowWithSpans = { ...rows[k] };
      if (k === i) base._groupRowSpan = span;
      else {
        base._skipTeam = true;
        base._skipMatter = true;
        base._skipUpcoming = true;
        base._skipNote = true;
      }
      out.push(base);
    }

    i = j;
  }

  return out;
};

const isOverdue = (value?: string | null): boolean =>
  value?.toLowerCase() === "overdue";

type LitigationReportTableProps = {
  data: ReportModel[];
  matterOptions: MatterData[];
	onDirtyChange?: (dirty: boolean) => void;
};

export type LitigationReportTableHandle = {
  saveAllNotes: () => Promise<void>;
  hasDirty: () => boolean;
};

export const LitigationReportTable = forwardRef<
  LitigationReportTableHandle,
  LitigationReportTableProps
>(({ data, matterOptions, onDirtyChange }, ref) => {
  const rows = useMemo(() => {
    const flat = data.flatMap((d) => d.reportLevel2 ?? []);
    return computeMatterGroupSpans(flat);
  }, [data]);

  const [notesByMatter, setNotesByMatter] = useState<Record<string, string>>({});
  const [savedNotesByMatter, setSavedNotesByMatter] = useState<Record<string, string>>({});
  const [savingByMatter, setSavingByMatter] = useState<Record<string, boolean>>({});
	const [savingAll, setSavingAll] = useState(false);

  const matterMap = useMemo(
    () => new Map(matterOptions.map(m => [m.id, m.display_number])),
    [matterOptions]
  );

  const matterNameToId = useMemo(() => {
    const map = new Map<string, number>();
        for (const m of matterOptions) {
        // normalize to avoid whitespace/case mismatch
        const key = String(m.display_number ?? "").trim().toLowerCase();
        if (key) map.set(key, m.id);
    }
    return map;
  }, [matterOptions]);

  const matterIdToName = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of matterOptions) map.set(m.id, String(m.display_number ?? ""));
    return map;
  }, [matterOptions]);

	const getDirtyMatterKeys = (): string[] => {
		return Object.keys(notesByMatter).filter((mk) => {
			const cur = notesByMatter[mk] ?? "";
			const saved = savedNotesByMatter[mk] ?? "";
			return cur !== saved;
		});
	};

	const saveAllNotes = async (): Promise<void> => {
		const dirtyKeys = getDirtyMatterKeys();
		if (dirtyKeys.length === 0) return;

		setSavingAll(true);
		try {
			// build payloads for dirty notes only
			const payloads: ReportNoteRequest[] = dirtyKeys
				.map((matterKey) => {
					const note = notesByMatter[matterKey] ?? "";
					const matterId = matterNameToId.get(matterKey.trim().toLowerCase());
					if (!matterId) return null;

					const matterName = matterIdToName.get(matterId) ?? matterKey;
					return { matterId, matterName, note } as ReportNoteRequest;
				})
				.filter((p): p is ReportNoteRequest => p !== null);

			// save them (parallel)
			await Promise.all(payloads.map((p) => ClioService.postApiClioSaveReportNote(p)));

			// mark as saved
			setSavedNotesByMatter((prev) => ({ ...prev, ...notesByMatter }));
		} catch (e) {
			console.error("Failed to save notes", e);
		} finally {
			setSavingAll(false);
		}
	};

	const hasDirty = useMemo(() => getDirtyMatterKeys().length > 0, [notesByMatter, savedNotesByMatter]);

	useImperativeHandle(ref, () => ({
		saveAllNotes,
		hasDirty: () => getDirtyMatterKeys().length > 0,
	}));

	const dirty = useMemo(
		() => Object.keys(notesByMatter).some((mk) => (notesByMatter[mk] ?? "") !== (savedNotesByMatter[mk] ?? "")),
		[notesByMatter, savedNotesByMatter]
	);

	useEffect(() => {
		onDirtyChange?.(dirty);
	}, [dirty, onDirtyChange]);


	useEffect(() => {
		if (rows.length === 0) return;
		if (matterOptions.length === 0) return;

		// matters currently visible in table
		const matterKeysInTable = Array.from(
			new Set(
				rows
					.map((r) => String(r.level2Data2 ?? "").trim())
					.filter((v) => v.length > 0)
			)
		);

		// resolve those matter names -> ids
		const matterIdsInTable = new Set<number>();
		for (const mk of matterKeysInTable) {
			const id = matterNameToId.get(mk.toLowerCase());
			if (typeof id === "number") matterIdsInTable.add(id);
		}

		if (matterIdsInTable.size === 0) return;

		let cancelled = false;

		(async () => {
			try {
				const apiRows = (await ClioService.getApiClioReportNotes()) as ReportNoteResponse[];
				console.log(apiRows)

				if (cancelled) return;

				// filter response to only matter ids used in this table
				const noteByMatterId = new Map<number, string>();
				for (const r of apiRows) {
					if (!matterIdsInTable.has(r?.matterId)) continue;
					noteByMatterId.set(r?.matterId, String(r.note ?? ""));
				}

				// hydrate your state keyed by matterKey (display_number)
				const next: Record<string, string> = {};
				for (const mk of matterKeysInTable) {
					const id = matterNameToId.get(mk.toLowerCase());
					if (!id) continue;
					next[mk] = noteByMatterId.get(id) ?? "";
				}

				setNotesByMatter((prev) => ({ ...prev, ...next }));
				setSavedNotesByMatter((prev) => ({ ...prev, ...next }));
			} catch (e) {
				console.error("Failed to load report notes", e);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [rows, matterOptions, matterNameToId]);

  const isDirty = (matterKey: string): boolean =>
    (notesByMatter[matterKey] ?? "") !== (savedNotesByMatter[matterKey] ?? "");

	const saveNoteApi = async (payload: ReportNoteRequest): Promise<void> => {
		await ClioService.postApiClioSaveReportNote(payload);
	};

	const handleSaveNote = async (matterKey: string): Promise<void> => {
			const note = notesByMatter[matterKey] ?? "";

			const normalizedMatterKey = String(matterKey).trim().toLowerCase();
			const matterId = matterNameToId.get(normalizedMatterKey);

			if (!matterId) {
					console.warn("Could not resolve matterId for:", matterKey);
					return;
			}

			const matterName = matterIdToName.get(matterId) ?? matterKey;

			const payload: ReportNoteRequest = { matterId, matterName, note };

			setSavingByMatter((p) => ({ ...p, [matterKey]: true }));
			try {
					await saveNoteApi(payload);
					setSavedNotesByMatter((p) => ({ ...p, [matterKey]: note }));
			} finally {
					setSavingByMatter((p) => ({ ...p, [matterKey]: false }));
			}
	};
	
	const stopScrollBubble = (e: React.WheelEvent<HTMLDivElement>) => {
		const el = e.currentTarget;
		const atTop = el.scrollTop === 0;
		const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

		if ((atTop && e.deltaY < 0) || (atBottom && e.deltaY > 0)) {
			e.preventDefault(); // stops the page from scrolling
		}
	};


  return (
    <div
			onWheel={stopScrollBubble}
      style={{
        maxHeight: 800,
        overflow: "auto",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
				overscrollBehavior: "contain",
      }}
    >
      <table
        style={{
          borderCollapse: "collapse",
          width: "100%",
          tableLayout: "fixed",
        }}
      >
        <colgroup>
          <col style={{ width: "11%" }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "7%" }} />
          <col style={{ width: "7%" }} />
          <col style={{ width: "36%" }} />
          <col style={{ width: "5%" }} />
          <col style={{ width: "8%" }} />
        </colgroup>

        <thead>
          <tr>
            <Th>Team</Th>
            <Th>Matter</Th>
            <Th>Funnel</Th>
            <Th>Stage</Th>
            <Th>Task</Th>
            <Th>Assignee</Th>
            <Th>Due Date</Th>
            <Th>Upcoming Deadlines</Th>
            <Th>Overdue</Th>
            <Th>Note</Th>
          </tr>
        </thead>

        <tbody>
          {rows.map((r, idx) => {
            const overdue = isOverdue(r.level2Data9);
            const matterKey = String(r.level2Data2 ?? "");
            const saving = Boolean(savingByMatter[matterKey]);

            return (
              <tr key={idx}>
                {!r._skipTeam && (
                  <Td rowSpan={r._groupRowSpan}>
                    {normalizeText(r.level2Data1)}
                  </Td>
                )}

                {!r._skipMatter && (
                  <Td rowSpan={r._groupRowSpan}>
                    {normalizeText(r.level2Data2)}
                  </Td>
                )}

                <Td style={overdue ? overdueCellStyle : undefined}>
                  {normalizeText(r.level2Data3)}
                </Td>
                <Td style={overdue ? overdueCellStyle : undefined}>
                  {normalizeText(r.level2Data4)}
                </Td>
                <Td style={overdue ? overdueCellStyle : undefined}>
                  {normalizeText(r.level2Data5)}
                </Td>
                <Td style={overdue ? overdueCellStyle : undefined}>
                  {normalizeText(r.level2Data6)}
                </Td>
                <Td style={overdue ? overdueCellStyle : undefined}>
                  {normalizeText(r.level2Data7)}
                </Td>

                {!r._skipUpcoming && (
                  <Td rowSpan={r._groupRowSpan}>
                    {normalizeText(r.level2Data8)}
                  </Td>
                )}

                <Td style={overdue ? overdueCellStyle : undefined}>
                  {normalizeText(r.level2Data9)}
                </Td>

                {!r._skipNote && (
                  <Td rowSpan={r._groupRowSpan} style={{ padding: 8, position: "relative" }}>
                    <div 
											style={{
												position: "absolute",
												inset: 8,
												display: "flex", 
												}}
										>
                      <textarea
                        value={notesByMatter[matterKey] ?? ""}
                        onChange={(e) =>
                          setNotesByMatter((p) => ({
                            ...p,
                            [matterKey]: e.target.value,
                          }))
                        }
                        placeholder="Add note…"
                        style={{
                          flex: 1,
                          height: "100%",
                          resize: "none",
                          border: "1px solid #cbd5e1",
                          borderRadius: 6,
                          padding: 8,
                          fontFamily: "inherit",
                          fontSize: "inherit",
                          lineHeight: 1.4,
                          boxSizing: "border-box",
                        }}
                      />

                    </div>
                  </Td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
});

const overdueCellStyle: React.CSSProperties = {
  backgroundColor: "#d7d8c7",
  fontWeight: 400,
};

const Th = ({ children }: { children: React.ReactNode }) => (
  <th
    style={{
      border: "1px solid #ddd",
      padding: 8,
      textAlign: "left",
      verticalAlign: "top",
      whiteSpace: "nowrap",
      backgroundColor: "#78808b",
      color: "#ffffff",
      fontWeight: 450,
      position: "sticky",
      top: 0,
      zIndex: 2,
    }}
  >
    {children}
  </th>
);

const Td = ({
  children,
  style,
  rowSpan,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  rowSpan?: number;
}) => (
  <td
    rowSpan={rowSpan}
    style={{
      border: "1px solid #ddd",
      padding: 8,
      verticalAlign: "top",
      whiteSpace: "pre-wrap",
      overflowWrap: "anywhere",
      wordBreak: "break-word",
			height: "100%",
      ...style,
    }}
  >
    {children}
  </td>
);
